import React, { useState, useEffect, useCallback } from 'react';
import { Badge, Dropdown } from 'react-bootstrap';
import axiosClient from '../../services/axiosClient';
import Swal from 'sweetalert2';

function getAuthHeader() {
	try {
		const str = localStorage.getItem('userDetails');
		if (!str) return {};
		const { token, tokenType } = JSON.parse(str) || {};
		if (token) return { Authorization: `${tokenType || 'Bearer'} ${token}` };
		return {};
	} catch (_) {
		return {};
	}
}

const Promotions = () => {
	const [promotions, setPromotions] = useState([]);
	const [filteredPromotions, setFilteredPromotions] = useState([]);
	const [loading, setLoading] = useState(true);
	const [searchTerm, setSearchTerm] = useState('');
	const [currentPage, setCurrentPage] = useState(1);
	const [perPage, setPerPage] = useState(15);
	const [totalPages, setTotalPages] = useState(1);
	const [statusFilter, setStatusFilter] = useState('all');
	const [sortBy, setSortBy] = useState('date');
	const [sortOrder, setSortOrder] = useState('desc');
	const [actionLoading, setActionLoading] = useState(null);

	const fetchPromotions = useCallback(async () => {
		try {
			setLoading(true);
			const response = await axiosClient.get(
				`/admin/payments/promotions?page=${currentPage}&per_page=${perPage}&status=${statusFilter}&sort=${sortBy}&order=${sortOrder}`,
				{
					headers: { 'Accept': 'application/json', ...getAuthHeader() },
				}
			);
			
			// Parse la structure de l'API
			const apiData = response?.data;
			const result = apiData?.result || {};
			let list = result?.data || [];
			
			if (!Array.isArray(list)) {
				list = [];
			}
			
			setPromotions(list);
			setFilteredPromotions(list);
			setTotalPages(result?.last_page || 1);
		} catch (err) {
			console.error('Error fetching promotions:', err);
		} finally {
			setLoading(false);
		}
	}, [currentPage, perPage, statusFilter, sortBy, sortOrder]);

	useEffect(() => {
		fetchPromotions();
	}, [fetchPromotions]);

	useEffect(() => {
		let filtered = [...promotions];
		if (searchTerm) {
			filtered = filtered.filter(p =>
				p.ad?.toLowerCase().includes(searchTerm.toLowerCase()) ||
				p.id?.toString().includes(searchTerm)
			);
		}
		setFilteredPromotions(filtered);
	}, [searchTerm, promotions]);

	const handleApprove = async (id) => {
		const result = await Swal.fire({
			title: 'Approuver la promotion',
			text: 'Voulez-vous vraiment approuver cette promotion ?',
			icon: 'question',
			showCancelButton: true,
			confirmButtonColor: '#28a745',
			cancelButtonColor: '#6c757d',
			confirmButtonText: 'Oui, approuver',
			cancelButtonText: 'Annuler',
			reverseButtons: true
		});

		if (!result.isConfirmed) return;
		
		try {
			setActionLoading(id);
			await axiosClient.put(
				`/admin/payments/${id}/approval`,
				{ active: true },
				{
					headers: { 
						'Accept': 'application/json',
						'Content-Type': 'application/json',
						...getAuthHeader() 
					},
				}
			);
			
			// Refresh the list
			await fetchPromotions();
			
			Swal.fire({
				title: 'Approuvée !',
				text: 'La promotion a été approuvée avec succès.',
				icon: 'success',
				confirmButtonColor: '#28a745',
				timer: 2000
			});
		} catch (err) {
			console.error('Error approving promotion:', err);
			const errorMsg = err.response?.data?.message || 'Erreur lors de l\'approbation de la promotion';
			
			Swal.fire({
				title: 'Erreur !',
				text: errorMsg,
				icon: 'error',
				confirmButtonColor: '#dc3545'
			});
		} finally {
			setActionLoading(null);
		}
	};

	const handleReject = async (id) => {
		const result = await Swal.fire({
			title: 'Rejeter la promotion',
			text: 'Voulez-vous vraiment rejeter cette promotion ?',
			icon: 'warning',
			showCancelButton: true,
			confirmButtonColor: '#dc3545',
			cancelButtonColor: '#6c757d',
			confirmButtonText: 'Oui, rejeter',
			cancelButtonText: 'Annuler',
			reverseButtons: true
		});

		if (!result.isConfirmed) return;
		
		try {
			setActionLoading(id);
			await axiosClient.put(
				`/admin/payments/${id}/approval`,
				{ active: false },
				{
					headers: { 
						'Accept': 'application/json',
						'Content-Type': 'application/json',
						...getAuthHeader() 
					},
				}
			);
			
			// Refresh the list
			await fetchPromotions();
			
			Swal.fire({
				title: 'Rejetée !',
				text: 'La promotion a été rejetée avec succès.',
				icon: 'success',
				confirmButtonColor: '#28a745',
				timer: 2000
			});
		} catch (err) {
			console.error('Error rejecting promotion:', err);
			const errorMsg = err.response?.data?.message || 'Erreur lors du rejet de la promotion';
			
			Swal.fire({
				title: 'Erreur !',
				text: errorMsg,
				icon: 'error',
				confirmButtonColor: '#dc3545'
			});
		} finally {
			setActionLoading(null);
		}
	};

	const getStatusBadge = (status) => {
		const statusMap = {
			'valid': { bg: 'success', text: 'Valide' },
			'pending': { bg: 'warning', text: 'En attente' },
			'expired': { bg: 'danger', text: 'Expiré' },
			'cancelled': { bg: 'secondary', text: 'Annulé' },
		};
		const config = statusMap[status] || { bg: 'secondary', text: status };
		return <Badge bg={config.bg}>{config.text}</Badge>;
	};

	const getPaymentMethodBadge = (method) => {
		const methodMap = {
			'offlinepayment': { bg: 'primary', text: 'Paiement hors ligne' },
			'card': { bg: 'success', text: 'Carte bancaire' },
			'mobile': { bg: 'info', text: 'Mobile money' },
			'paypal': { bg: 'warning', text: 'PayPal' },
		};
		const config = methodMap[method] || { bg: 'secondary', text: method };
		return <Badge bg={config.bg}>{config.text}</Badge>;
	};

	const formatDate = (dateString) => {
		if (!dateString) return '-';
		const date = new Date(dateString);
		return date.toLocaleDateString('fr-FR', { 
			year: 'numeric', 
			month: '2-digit', 
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit'
		});
	};

	return (
		<>
			<style>
				{`
					.promotions-table {
						background: white;
						border-radius: 12px;
						overflow: hidden;
						box-shadow: 0 2px 8px rgba(0,0,0,0.08);
					}
					
					.promotions-table thead {
						background: linear-gradient(135deg, #1e7e34 0%, #28a745 100%);
						color: white;
					}
					
					.promotions-table thead th {
						font-weight: 600;
						padding: 16px 12px;
						border: none;
						font-size: 14px;
						text-transform: uppercase;
						letter-spacing: 0.5px;
					}
					
					.promotions-table tbody tr {
						border-bottom: 1px solid #f0f0f0;
						transition: all 0.2s ease;
					}
					
					.promotions-table tbody tr:hover {
						background-color: #f0fdf4;
						transform: translateX(2px);
					}
					
					.promotions-table tbody td {
						padding: 14px 12px;
						vertical-align: middle;
						font-size: 14px;
					}
					
					.promotion-id {
						display: inline-flex;
						align-items: center;
						justify-content: center;
						width: 32px;
						height: 32px;
						background: linear-gradient(135deg, #1e7e34 0%, #28a745 100%);
						color: white;
						border-radius: 50%;
						font-weight: 600;
						font-size: 13px;
					}
					
					.promotion-ad {
						color: #1e7e34;
						font-weight: 500;
						text-decoration: none;
						cursor: pointer;
					}
					
					.promotion-ad:hover {
						text-decoration: underline;
						color: #28a745;
					}
					
					.package-badge {
						display: inline-block;
						padding: 4px 12px;
						background: #dcfce7;
						color: #166534;
						border-radius: 20px;
						font-size: 13px;
						font-weight: 500;
					}
					
					.filter-btn {
						border: 2px solid #e5e7eb;
						border-radius: 8px;
						padding: 8px 16px;
						background: white;
						color: #374151;
						font-weight: 500;
						transition: all 0.2s;
					}
					
					.filter-btn:hover {
						border-color: #1e7e34;
						color: #1e7e34;
					}
					
					.filter-btn.active {
						background: linear-gradient(135deg, #1e7e34 0%, #28a745 100%);
						color: white;
						border-color: transparent;
					}
					
					.pagination-modern {
						display: flex;
						gap: 8px;
						align-items: center;
					}
					
					.pagination-modern button {
						border: 2px solid #e5e7eb;
						border-radius: 8px;
						padding: 8px 14px;
						background: white;
						color: #374151;
						font-weight: 500;
						transition: all 0.2s;
					}
					
					.pagination-modern button:hover:not(:disabled) {
						border-color: #1e7e34;
						color: #1e7e34;
					}
					
					.pagination-modern button.active {
						background: linear-gradient(135deg, #1e7e34 0%, #28a745 100%);
						color: white;
						border-color: transparent;
					}
					
					.pagination-modern button:disabled {
						opacity: 0.5;
						cursor: not-allowed;
					}
					
					.action-btn {
						padding: 6px 12px;
						border-radius: 6px;
						border: none;
						font-size: 13px;
						font-weight: 500;
						cursor: pointer;
						transition: all 0.2s;
						margin: 0 2px;
					}
					
					.action-btn-view {
						background: #e0f2fe;
						color: #0369a1;
					}
					
					.action-btn-view:hover {
						background: #0369a1;
						color: white;
					}
					
					.action-btn-approve {
						background: #dcfce7;
						color: #166534;
					}
					
					.action-btn-approve:hover {
						background: #166534;
						color: white;
					}
					
					.action-btn-reject {
						background: #fee2e2;
						color: #991b1b;
					}
					
					.action-btn-reject:hover {
						background: #991b1b;
						color: white;
					}
				`}
			</style>

			{/* Header */}
			<div className="row mb-4">
				<div className="col-12">
					<div className="d-flex justify-content-between align-items-center">
						<div>
							<h2 style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937' }}>
								<i className="fas fa-bullhorn me-3" style={{ color: '#1e7e34' }}></i>
								Promotions
							</h2>
							<p className="text-muted">Gestion des paiements et promotions</p>
						</div>
					</div>
				</div>
			</div>

			{/* Filters */}
			<div className="row mb-4">
				<div className="col-12">
					<div className="d-flex gap-3 flex-wrap align-items-center">
						{/* Status Filter */}
						<div className="d-flex gap-2">
							<button 
								className={`filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
								onClick={() => setStatusFilter('all')}
							>
								Tous
							</button>
							<button 
								className={`filter-btn ${statusFilter === 'pending' ? 'active' : ''}`}
								onClick={() => setStatusFilter('pending')}
							>
								En attente
							</button>
							<button 
								className={`filter-btn ${statusFilter === 'approved' ? 'active' : ''}`}
								onClick={() => setStatusFilter('approved')}
							>
								Approuvé
							</button>
							<button 
								className={`filter-btn ${statusFilter === 'rejected' ? 'active' : ''}`}
								onClick={() => setStatusFilter('rejected')}
							>
								Rejeté
							</button>
						</div>

						{/* Sort */}
						<Dropdown>
							<Dropdown.Toggle variant="outline-secondary" className="filter-btn">
								<i className="fas fa-sort me-2"></i>
								Trier par: {sortBy === 'date' ? 'Date' : 'ID'}
							</Dropdown.Toggle>
							<Dropdown.Menu>
								<Dropdown.Item onClick={() => setSortBy('date')}>Date</Dropdown.Item>
								<Dropdown.Item onClick={() => setSortBy('id')}>ID</Dropdown.Item>
							</Dropdown.Menu>
						</Dropdown>

						{/* Order */}
						<button 
							className="filter-btn"
							onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
						>
							<i className={`fas fa-arrow-${sortOrder === 'asc' ? 'up' : 'down'} me-2`}></i>
							{sortOrder === 'asc' ? 'Croissant' : 'Décroissant'}
						</button>
					</div>
				</div>
			</div>

			{/* Per Page & Search */}
			<div className="row mb-3">
				<div className="col-md-6">
					<Dropdown>
						<Dropdown.Toggle variant="outline-secondary" size="sm" style={{ borderRadius: '8px' }}>
							{perPage} par page
						</Dropdown.Toggle>
						<Dropdown.Menu>
							<Dropdown.Item onClick={() => setPerPage(10)}>10</Dropdown.Item>
							<Dropdown.Item onClick={() => setPerPage(15)}>15</Dropdown.Item>
							<Dropdown.Item onClick={() => setPerPage(25)}>25</Dropdown.Item>
							<Dropdown.Item onClick={() => setPerPage(50)}>50</Dropdown.Item>
						</Dropdown.Menu>
					</Dropdown>
				</div>
				<div className="col-md-6">
					<div className="d-flex justify-content-end">
						<div className="input-group" style={{ maxWidth: '350px' }}>
							<span className="input-group-text bg-white" style={{ borderRadius: '8px 0 0 8px' }}>
								<i className="fas fa-search"></i>
							</span>
							<input 
								type="text" 
								className="form-control" 
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								placeholder="Rechercher par ID ou annonce..."
								style={{ borderRadius: '0 8px 8px 0' }}
							/>
						</div>
					</div>
				</div>
			</div>

			{/* Table */}
			<div className="promotions-table">
				{loading ? (
					<div className="text-center py-5">
						<div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }}></div>
						<p className="mt-3 text-muted">Chargement des promotions...</p>
					</div>
				) : (
					<table className="table table-hover mb-0">
						<thead>
							<tr>
								<th style={{ width: '80px' }}>ID</th>
								<th style={{ width: '140px' }}>Date</th>
								<th>Annonce</th>
								<th style={{ width: '150px' }}>Package</th>
								<th style={{ width: '180px' }}>Moyen de paiement</th>
								<th style={{ width: '120px' }}>Statut</th>
								<th style={{ width: '100px' }}>Actions</th>
							</tr>
						</thead>
						<tbody>
							{filteredPromotions.length === 0 ? (
								<tr>
									<td colSpan="7" className="text-center py-5">
										<i className="fas fa-inbox fa-3x text-muted mb-3"></i>
										<p className="text-muted">Aucune promotion trouvée</p>
									</td>
								</tr>
							) : (
								filteredPromotions.map((promo) => (
									<tr key={promo.id}>
										<td>
											<span className="promotion-id">{promo.id}</span>
										</td>
										<td style={{ fontSize: '13px', color: '#6b7280' }}>
											{formatDate(promo.date)}
										</td>
										<td>
											<a href={`#/ad/${promo.post?.id}`} className="promotion-ad">
												{promo.post?.title || `#${promo.post?.id} - Annonce`}
											</a>
											<div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
												{promo.post?.company_name}
											</div>
										</td>
										<td>
											<span className="package-badge">
												{promo.package?.name || 'Gratuit'}
											</span>
											<div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
												{promo.amount} {promo.currency_code}
											</div>
										</td>
										<td>
											{getPaymentMethodBadge(promo.payment_method)}
										</td>
										<td>
											<div className="d-flex flex-column gap-1">
												{getStatusBadge(promo.status)}
												{promo.approved ? (
													<Badge bg="success" style={{ fontSize: '10px' }}>
														<i className="fas fa-check-circle me-1"></i>Approuvé
													</Badge>
												) : (
													<Badge bg="warning" style={{ fontSize: '10px' }}>
														<i className="fas fa-clock me-1"></i>En attente
													</Badge>
												)}
											</div>
										</td>
										<td>
											<div className="d-flex gap-1">
												<button 
													className="action-btn action-btn-view"
													title="Voir détails"
													onClick={() => window.location.href = `#/promotion/${promo.id}`}
													disabled={actionLoading === promo.id}
												>
													<i className="fas fa-eye"></i>
												</button>
												
												{/* Afficher le bouton Approuver seulement si non approuvé */}
												{!promo.approved && (
													<button 
														className="action-btn action-btn-approve"
														title="Approuver"
														onClick={() => handleApprove(promo.id)}
														disabled={actionLoading === promo.id}
													>
														{actionLoading === promo.id ? (
															<span className="spinner-border spinner-border-sm"></span>
														) : (
															<i className="fas fa-check"></i>
														)}
													</button>
												)}
												
												{/* Afficher le bouton Rejeter seulement si approuvé */}
												{promo.approved && (
													<button 
														className="action-btn action-btn-reject"
														title="Désapprouver"
														onClick={() => handleReject(promo.id)}
														disabled={actionLoading === promo.id}
													>
														{actionLoading === promo.id ? (
															<span className="spinner-border spinner-border-sm"></span>
														) : (
															<i className="fas fa-times"></i>
														)}
													</button>
												)}
											</div>
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				)}
			</div>

			{/* Pagination */}
			{!loading && filteredPromotions.length > 0 && (
				<div className="row mt-4">
					<div className="col-12">
						<div className="d-flex justify-content-between align-items-center">
							<p className="text-muted mb-0">
								Page {currentPage} sur {totalPages}
							</p>
							<div className="pagination-modern">
								<button 
									onClick={() => setCurrentPage(1)}
									disabled={currentPage === 1}
								>
									<i className="fas fa-angle-double-left"></i>
								</button>
								<button 
									onClick={() => setCurrentPage(currentPage - 1)}
									disabled={currentPage === 1}
								>
									<i className="fas fa-angle-left"></i>
								</button>
								
								{[...Array(Math.min(5, totalPages))].map((_, i) => {
									let pageNum;
									if (totalPages <= 5) {
										pageNum = i + 1;
									} else if (currentPage <= 3) {
										pageNum = i + 1;
									} else if (currentPage >= totalPages - 2) {
										pageNum = totalPages - 4 + i;
									} else {
										pageNum = currentPage - 2 + i;
									}
									
									return (
										<button 
											key={i}
											onClick={() => setCurrentPage(pageNum)}
											className={currentPage === pageNum ? 'active' : ''}
										>
											{pageNum}
										</button>
									);
								})}
								
								<button 
									onClick={() => setCurrentPage(currentPage + 1)}
									disabled={currentPage === totalPages}
								>
									<i className="fas fa-angle-right"></i>
								</button>
								<button 
									onClick={() => setCurrentPage(totalPages)}
									disabled={currentPage === totalPages}
								>
									<i className="fas fa-angle-double-right"></i>
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</>
	);
};

export default Promotions;
